import { NavigationLinkStatus } from "@/components/navigation/NavigationFeedback/NavigationLinkStatus";
import { cva } from "class-variance-authority";
import Link from "next/link";
import cn from "twsx";

// The one place domain displays get their type: 13/500 primary text with a
// tertiary number, or 12px/11px in the compact `sm` density used by dense
// table cells. Links take the token link colour.

type DisplaySize = "md" | "sm";

type DisplayEmptyStateProps = {
  children?: React.ReactNode;
};

type InlineIdentityDisplayProps = {
  overflow?: "truncate" | "wrap";
  size?: DisplaySize;
  media?: React.ReactNode;
  primary?: React.ReactNode;
  secondary?: React.ReactNode;
  trailing?: React.ReactNode;
  onClick?: () => void;
  href?: string;
  className?: string;
  emptyState?: React.ReactNode;
};

type InlineValueDisplayProps = {
  leading?: React.ReactNode;
  value?: React.ReactNode;
  tone?: "default" | "muted";
  className?: string;
  emptyState?: React.ReactNode;
};

const primaryText = cva("min-w-0 font-medium", {
  variants: {
    size: {
      md: "text-base leading-5",
      sm: "text-sm leading-4",
    },
    overflow: {
      truncate: "truncate",
      wrap: "whitespace-normal [overflow-wrap:anywhere]",
    },
    link: {
      true: "text-text-link",
      false: "text-text-primary",
    },
  },
});

const secondaryText = cva("shrink-0", {
  variants: {
    size: {
      md: "text-base leading-5",
      sm: "text-xs leading-4",
    },
    link: {
      true: "text-text-link",
      false: "text-text-tertiary",
    },
  },
});

function hasDisplayContent(value: React.ReactNode | undefined) {
  return value !== null && value !== undefined && value !== "";
}

export function formatDisplayNumber(value?: number | string | null) {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  return `(#${value})`;
}

export function DisplayEmptyState({ children = "-" }: DisplayEmptyStateProps) {
  return <span className="text-base text-text-tertiary">{children}</span>;
}

export function InlineIdentityDisplay({
  overflow = "truncate",
  size = "md",
  media,
  primary,
  secondary,
  trailing,
  onClick,
  href,
  className,
  emptyState = "-",
}: InlineIdentityDisplayProps) {
  if (!hasDisplayContent(primary)) {
    return <DisplayEmptyState>{emptyState}</DisplayEmptyState>;
  }

  const link = Boolean(href);
  const content = (
    <>
      {media ? <span className="flex shrink-0 items-center">{media}</span> : null}
      {overflow === "truncate"
        ? (
          <span className={primaryText({ size, overflow, link })}>
            <span>{primary}</span>
            {hasDisplayContent(secondary) ? <> <span className={cn(secondaryText({ size, link }), "font-normal")}>{secondary}</span></> : null}
          </span>
        )
        : (
          <span className="flex min-w-0 flex-wrap items-center gap-1">
            <span className={primaryText({ size, overflow, link })}>{primary}</span>
            {hasDisplayContent(secondary) ? <span className={secondaryText({ size, link })}>{secondary}</span> : null}
          </span>
        )}
      {hasDisplayContent(trailing) ? <span className={secondaryText({ size, link: false })}>{trailing}</span> : null}
    </>
  );

  const identityClassName = cn(
    "flex min-w-0 max-w-full items-center gap-1.5",
    overflow === "wrap" && "items-start",
    (onClick || href) && "cursor-pointer",
    href && "rounded-kbd hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
    className,
  );

  if (href) {
    const linkProps = {
      className: identityClassName,
      onClick: (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.stopPropagation();
        onClick?.();
      },
    };

    if (href.startsWith("/") && !href.startsWith("//")) {
      return (
        <Link href={href} prefetch={false} {...linkProps}>
          {content}
          <NavigationLinkStatus href={href} />
        </Link>
      );
    }

    return (
      <a href={href} {...linkProps}>
        {content}
      </a>
    );
  }

  return (
    <div className={identityClassName} onClick={onClick}>
      {content}
    </div>
  );
}

export function InlineValueDisplay({
  leading,
  value,
  tone = "default",
  className,
  emptyState = "-",
}: InlineValueDisplayProps) {
  if (!hasDisplayContent(value)) {
    return <DisplayEmptyState>{emptyState}</DisplayEmptyState>;
  }

  return (
    <span className={cn("inline-flex min-w-0 items-center gap-1.5", className)}>
      {leading ? <span className="flex shrink-0 items-center">{leading}</span> : null}
      <span
        className={cn(
          "truncate text-base leading-5",
          tone === "muted" ? "text-text-tertiary" : "font-medium text-text-primary",
        )}
      >
        {value}
      </span>
    </span>
  );
}
